/**
 * Document type keys accepted by the unified document pipeline
 * (WebAPI DocumentPipelineController / DocumentTypeRegistry).
 *
 * Values are the API's shared DocumentConstants — the same strings as the
 * Document table's Description column and the tms-web ReportNames enum, so
 * there is one naming scheme across the stack. A document type must also be
 * enabled in the API's DocumentPipelineSettings.EnabledDocumentTypes before
 * it can be used.
 *
 * HOW THIS DIFFERS FROM ReportNames (tms-web shared/constants/report-names.ts):
 * ReportNames addresses Document TABLE rows — getDocument() email messages and
 * getUserPrinterForDocument() printer config — and predates the pipeline.
 * PipelineDocumentTypes addresses the RENDER registry: which server-side
 * document to produce. Most values coincide (one naming scheme), but each list
 * has entries the other lacks (e.g. pipeline-only "Sales Picking List",
 * "Picked Customer Stock Labels"; ReportNames-only rows that only carry email
 * text). They can merge once the legacy client-side report paths are deleted.
 */
export const PipelineDocumentTypes = {
  salesInvoice: 'Sales Invoice',
  creditNote: 'Credit Note',
  salesOrderConfirmation: 'Sales Order Confirmation',
  salesDespatchNote: 'Sales Despatch Note',
  proformaInvoice: 'Proforma Invoice',
  purchaseOrder: 'Purchase Order',
  pickingList: 'Picking List',
  purchaseDespatch: 'Purchase Despatch',
  goodsDeliveryNote: 'Goods Delivery Note',
  salesManifest: 'Sales Manifest',
  commercialInvoice: 'Commercial Invoice',
  archiveInvoice: 'Archive Invoice',
  supplementaryInvoice: 'Supplementary Invoice',
  availableStockPickingList: 'Available Stock Picking List',
  salesOrderBalances: 'Sales Order Balances',
  salesBackOrder: 'Sales Back Order',
  paymentInvoice: 'Payment Invoice',
  // The sales-order picking list — a different document from pickingList
  // (the purchase-order one), despite the similar name.
  salesPickingList: 'Sales Picking List',
  releaseOrder: 'Release Order',
  salesOrderCollection: 'Sales Order Collection',
  stockLabel: 'Stock Label',
  salesContract: 'Sales Contract',
  salesPack: 'Sales Pack',
  purchaseInvoice: 'Purchase Invoice',
  purchaseContract: 'Purchase Contract',
  // The third-party despatch note on the purchase order Reports tab.
  despatchNote: 'Despatch Note',
  // The purchase-receipt goods received note; its reportId is the delivery
  // note NUMBER (a string), which the pipeline sends as reportKey.
  purchaseDeliveryNote: 'Purchase Delivery Note',
  releaseStockDocument: 'Release Stock Document',
  productSpecPrint: 'Product Spec Print',
  locationLabel: 'Location Label',
  // All PICKED customer stock labels for a sales order, one label per page.
  pickedCustomerStockLabels: 'Picked Customer Stock Labels',
  // Customer stock labels for a despatch note's items, one label per page.
  despatchCustomerStockLabels: 'Despatch Customer Stock Labels',
  // Delivery address label (with the Order No barcode) for a despatch note.
  addressLabel: 'Address Label',
  // Supplier stock labels for stock allocated to a purchase order's BOMs.
  supplierStockLabels: 'Supplier Stock Labels',
} as const;
